import type { CSSProperties } from 'react';

import { tokenVar } from '@shared/design-system';

/** Figma `Dialog` 세트(Components, 34:448)의 축 */
export const DIALOG_SIZES = ['xs', 'sm', 'md', 'lg'] as const;
export type DialogSize = (typeof DIALOG_SIZES)[number];

export const DIALOG_STATUSES = ['default', 'loading', 'init'] as const;
export type DialogStatus = (typeof DIALOG_STATUSES)[number];

/* ── 겹쳐 뜨는 틀(ModalLayer) ───────────────────────────── */

export type ModalLayerPlacement = 'center' | 'right' | 'left' | 'bottom';
export type ModalLayerZ = 'modal' | 'overlay';

export const MODAL_LAYER_Z_CLASS: Record<ModalLayerZ, string> = {
  modal: 'z-(--z-modal)',
  overlay: 'z-(--z-overlay)',
};

/**
 * 덮개 안에서 판을 놓는 방식. 판의 높이는 화면 안으로 제한한다:
 * center = 화면 − 바깥 여백 20(`p-5`) 사방, bottom = 화면 − 위 80(`pt-20`), 좌우 = 화면 높이 그대로.
 */
export const MODAL_LAYER_WRAPPER_CLASS: Record<ModalLayerPlacement, string> = {
  center: 'flex items-center justify-center p-5',
  right: '',
  left: '',
  bottom: 'flex flex-col justify-end pt-20',
};

/**
 * portal 로 띄울 때 판의 자리. 판 안의 스크롤은 판(Dialog · AlertDialog · Drawer)의 본문이 맡는다
 * (GraphCard 확대 팝업도 2026-10-01 부터 Dialog 라 판 자체는 스크롤하지 않는다).
 */
export const MODAL_LAYER_PANEL_CLASS: Record<ModalLayerPlacement, string> = {
  center: 'relative max-h-full max-w-full',
  right: 'absolute inset-y-0 right-0',
  left: 'absolute inset-y-0 left-0',
  bottom: 'relative max-h-full w-full',
};

/* ── Dialog ─────────────────────────────────────────────── */

/**
 * 판: 흰 면 · 모서리 12(radius/2xl) · elevation/card. 머리 60(size/row) · 본문 32×20 · 바닥 32×20.
 * 너비 xs 400 · sm 600 · md 800 · lg 1200 = `size.dialog.*`.
 * 세로 = [머리(고정) | 머리 아래 줄(고정, 선택) | 본문(스크롤) | 바닥(고정)]. 판 높이는 ModalLayer 가 화면 안으로 제한한다.
 */
export const DIALOG_PANEL_CLASS =
  'relative flex flex-col overflow-hidden rounded-2xl bg-bg-surface text-fg-default shadow-card';

export const DIALOG_WIDTH_CLASS: Record<DialogSize, string> = {
  xs: 'w-(--size-dialog-xs)',
  sm: 'w-(--size-dialog-sm)',
  md: 'w-(--size-dialog-md)',
  lg: 'w-(--size-dialog-lg)',
};

export const DIALOG_HEADER_CLASS =
  'flex h-(--size-row) shrink-0 items-center justify-between gap-2 border-b border-border-subtle pr-5 pl-8';

/** 머리 오른쪽 버튼 자리(닫기 왼쪽) */
export const DIALOG_HEADER_ACTIONS_CLASS = 'flex shrink-0 items-center gap-2-5';

/** 머리 아래 고정 줄(`subheader`, 필터 · 탭 등). 본문과 같은 좌우 32, 위 20. 아래 간격은 본문의 위 16 */
export const DIALOG_SUBHEADER_CLASS = 'flex shrink-0 flex-col gap-4 px-8 pt-5';

/**
 * 본문: 넘치면 본문만 세로로 스크롤된다(머리 · 바닥은 고정). `min-h-0` 이라 판 높이 안으로 줄어든다.
 * `relative` — 안쪽의 absolute 요소(`sr-only` 등)가 판이 아니라 본문 안에 놓여 판을 넘치게 하지 않는다.
 * 스크롤바는 브라우저 기본(보임) — 앱의 다른 스크롤 영역과 같다.
 */
export const DIALOG_BODY_CLASS = 'relative flex min-h-0 flex-col gap-4 overflow-y-auto px-8 py-5';

/**
 * 머리 없는 판(AlertDialog 확인 창): 제목을 본문 안 첫 줄로 — 16 보통 굵기(body-lg), 설명과 사이 8.
 * 본문 위아래 20 · 좌우 32 는 DIALOG_BODY_CLASS 그대로(2026-10-07 사용자 — 굵은 18 제목 · 머리 줄을 없앰)
 */
export const DIALOG_HEADERLESS_BODY_CLASS = 'gap-2';
export const DIALOG_HEADERLESS_TITLE_CLASS = 'text-fg-default typo-body-lg';

/** `subheader` 가 있으면 본문 위 간격을 16 으로(머리 아래 줄과 본문 사이 = 본문 안 간격) */
export const DIALOG_BODY_AFTER_SUBHEADER_CLASS = 'pt-4';

export const DIALOG_FOOTER_CLASS =
  'flex shrink-0 items-center justify-center gap-5 border-t border-border-default px-8 py-5';

/** loading 때 머리 · 본문 글 · 바닥을 흐리게. Figma 는 40%(변수 없음) → 가장 가까운 opacity/disabled(50%) */
export const DIALOG_LOADING_DIM_CLASS = 'pointer-events-none opacity-(--opacity-disabled)';

/** 바닥 버튼 너비 — 취소: xs 120 · 나머지 140 / 확인 · 삭제하기: xs 120 · sm 240 · md · lg 300 */
export const DIALOG_CANCEL_BUTTON_CLASS: Record<DialogSize, string> = {
  xs: 'w-(--size-button-md)',
  sm: 'w-(--size-button-lg)',
  md: 'w-(--size-button-lg)',
  lg: 'w-(--size-button-lg)',
};

export const DIALOG_MAIN_BUTTON_CLASS: Record<DialogSize, string> = {
  xs: 'w-(--size-button-md)',
  sm: '',
  md: 'w-(--size-button-2xl)',
  lg: 'w-(--size-button-2xl)',
};

/** sm 의 240 은 한 곳에서만 쓰는 값이라 raw 예외(R4 — 가까운 단계 button.xl 200 · 2xl 300 에 맞추면 모양이 바뀐다) */
export const DIALOG_MAIN_BUTTON_STYLE: Partial<Record<DialogSize, CSSProperties>> = {
  sm: { width: 240 },
};

/**
 * status=init 의 막대(모서리 full · bg/pressed). 제목 200×20, 본문 12 높이 3줄(392 · 392 · 235, 좁으면 줄어든다).
 * 너비는 `size.skeleton.*`(DD-19)를 인라인 style 로.
 */
export const DIALOG_SKELETON_TITLE_WIDTH = tokenVar['size.skeleton.200'];
export const DIALOG_SKELETON_LINES_WIDTH = [
  tokenVar['size.skeleton.392'],
  tokenVar['size.skeleton.392'],
  tokenVar['size.skeleton.235'],
] as const;
